import { DateTime } from 'luxon'

import { AnyResultProfile, GroupedResultProfile, ProfileType } from '@modules/search/types'

import { CHAT_ID_TO_OPEN_KEY, CHAT_MODAL_OPEN_KEY, PREVIOUS_URL_PATHNAME_KEY, PREVIOUS_URL_SEARCH_KEY } from './constants'

// eslint-disable-next-line import/prefer-default-export,no-promise-executor-return
export const delay = (ms: number) => new Promise(res => setTimeout(res, ms))

export const isSubstringOf = (valueToTest: string, testValue?: string) =>
  valueToTest.toLocaleLowerCase().includes(testValue?.toLocaleLowerCase()?.trim() || '')

export const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

export const isElementInView = (rect: DOMRect | undefined, containingRect: DOMRect | undefined) =>
  !!rect && !!containingRect && rect.right > containingRect.left && rect.left < containingRect.right

export const removeObjProperty = (property: string, obj: Record<string, unknown>) => {
  const { [property]: _, ...rest } = obj

  return rest
}

export const numberWithCommas = (num: number | string) => String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

export const truncateWithDots = (str: string, maxLength = 25) => {
  const string = String(str)

  return string?.length > maxLength ? `${string.slice(0, maxLength)}...` : string
}

export const createEmptyArray = (
  length: number, // eslint-disable-next-line
) => Array.apply(null, Array(length))

export const calcStageProgress = (allStages: string[], currentStage: string) =>
  (100 / allStages.length) * (allStages.findIndex(i => i.toLowerCase() === currentStage.toLowerCase()) + 1)

export const capitalizeString = (str: string) => `${str.charAt(0).toUpperCase()}${str.slice(1)}`

export const parseJSONSafely = (objToParse: string) => {
  try {
    return JSON.parse(objToParse)
  } catch (e) {
    return null
  }
}

/** Temp solution to sanitize wrongly migrated data */
const TEMP_TYPE_MAP: Record<string, ProfileType> = {
  biotech_asset: ProfileType.biotech,
  medtech_asset: ProfileType.medtech,
  technology_asset: ProfileType.technologies,
  service_asset: ProfileType.services,
  company: ProfileType.company,
  delegate: ProfileType.delegate,
}

export const mapApiProfileTypeToUI = (profiles: AnyResultProfile[]) =>
  profiles
    .map(i => ({
      ...i,
      '@type': TEMP_TYPE_MAP[i['@type'] as string] || i['@type'],
      latest: { ...i.latest, id: i.latest.id || i.id, '@type': TEMP_TYPE_MAP[i['@type'] as string] || i['@type'] },
    }))
    .filter(i => !!i['@type'])

export const mapApiGroupedProfileTypeToUI = (groupes: GroupedResultProfile[]) =>
  groupes.map(group => ({
    company: {
      ...group.company,
      '@type': TEMP_TYPE_MAP[group.company['@type'] as string] || group.company['@type'],
      latest: {
        ...group.company.latest,
        id: group.company.latest.id || group.company.id,
        '@type': TEMP_TYPE_MAP[group.company['@type'] as string] || group.company['@type'],
      },
    },
    profiles: mapApiProfileTypeToUI(group.profiles),
  }))

export const isSameDay = (date1: DateTime, date2: DateTime) => {
  return date1.day === date2.day && date1.month === date2.month
}

export const createMapFromArr = <T>(arr: T[], getKeyCb: (i: T) => string) => {
  return arr.reduce((acc, item) => {
    const dateKey = getKeyCb(item)

    if (!acc[dateKey]) {
      acc[dateKey] = []
    }

    acc[dateKey].push(item)

    return acc
  }, {} as Record<string, T[]>)
}

export const isTodayDate = (date: string): boolean => {
  const now = DateTime.now()

  return DateTime.fromISO(date).hasSame(now, 'day')
}

export const isFutureDate = (date: string): boolean => {
  return DateTime.fromISO(date) > DateTime.now()
}

export const removeHtmlTags = (input: string) =>
  String(input)
    ?.replace(
      // eslint-disable-next-line
      /<\/?(div|p|h[1-6]|li|ul|ol|table|thead|tbody|tfoot|tr|th|td|blockquote|pre|hr|dl|dt|dd|figure|figcaption|main|nav|article|aside|section|header|footer|address|canvas|fieldset|legend|details|summary)[^>]*>/gi,
      ' ',
    )
    .replace(/<\/?[^>]+(>|$)/g, '')

export const persistUrlDataToLocalStorage = () => {
  localStorage.setItem(PREVIOUS_URL_PATHNAME_KEY, window.location.pathname)
  localStorage.setItem(PREVIOUS_URL_SEARCH_KEY, window.location.search)
}

export const retrieveUrlDataFromLocalStorage = () => {
  const pathname = localStorage.getItem(PREVIOUS_URL_PATHNAME_KEY)
  const search = localStorage.getItem(PREVIOUS_URL_SEARCH_KEY)

  return {
    pathname,
    search,
  }
}

export const clearUrlDataFromLocalStorage = () => {
  localStorage.removeItem(PREVIOUS_URL_PATHNAME_KEY)
  localStorage.removeItem(PREVIOUS_URL_SEARCH_KEY)
}

export const persistChatModalOpenToLocalStorage = () => {
  localStorage.setItem(CHAT_MODAL_OPEN_KEY, 'true')
}

export const retrieveChatModalOpenFromLocalStorage = () => {
  const chatModalOpenValue = localStorage.getItem(CHAT_MODAL_OPEN_KEY)

  return {
    chatModalOpen: Boolean(chatModalOpenValue),
  }
}

export const clearChatModalOpenFromLocalStorage = () => {
  localStorage.removeItem(CHAT_MODAL_OPEN_KEY)
}

export const persistChatIdToOpenToLocalStorage = (id: string) => {
  localStorage.setItem(CHAT_ID_TO_OPEN_KEY, id)
}

export const retrieveChatIdToOpenFromLocalStorage = () => {
  const chatIdToOpenValue = localStorage.getItem(CHAT_ID_TO_OPEN_KEY)

  return {
    chatIdToOpen: Number(chatIdToOpenValue),
  }
}

export const clearChatIdToOpenFromLocalStorage = () => {
  localStorage.removeItem(CHAT_ID_TO_OPEN_KEY)
}
